import tinycolor from "tinycolor2";
import { useDebounce } from "use-debounce";
import { useEffect, useState } from "react";
import invert, { RgbArray } from "invert-color";
import { useNotifier } from "react-headless-notifier";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "store";
import useTitle from "hooks/useTitle";
import ColourTypes from "types/colours";
import { setColours } from "slices/practice";
import ColourSection from "./sections/colour";
import { useUpdatePracticeThemeMutation } from "api";
import { clearTempColours, setTempColours } from "slices/themeColours";
import { Button, NotifierNotification, UnsavedChangesPrompt } from "@mwi/ui";

const ColoursPage = (): JSX.Element => {
  useTitle("Colours");

  const dispatch = useDispatch();
  const { notify } = useNotifier();

  const { practiceId, defaultPrimary, defaultSecondary } = useSelector(
    (state: RootState) => ({
      practiceId: state.practice.id,
      practiceTheme: state.practice.theme,
      defaultPrimary: state.practice.theme.primaryColor ?? "",
      defaultSecondary: state.practice.theme.lightColor ?? "",
    }),
  );

  const [primaryColour, setPrimaryColour] = useState<ColourTypes>(
    tinycolor(`rgb(${defaultPrimary})`).isValid()
      ? {
          hsl: tinycolor(`rgb(${defaultPrimary})`).toHsl(),
          hex: tinycolor(`rgb(${defaultPrimary})`).toString("hex"),
          rgb: tinycolor(`rgb(${defaultPrimary})`).toRgb(),
        }
      : {
          hsl: tinycolor("#3c289b").toHsl(),
          hex: "#3c289b",
          rgb: tinycolor("#3c289b").toRgb(),
        },
  );
  const [secondaryColour, setSecondaryColour] = useState<ColourTypes>(
    tinycolor(`rgb(${defaultSecondary})`).isValid()
      ? {
          hsl: tinycolor(`rgb(${defaultSecondary})`).toHsl(),
          hex: tinycolor(`rgb(${defaultSecondary})`).toString("hex"),
          rgb: tinycolor(`rgb(${defaultSecondary})`).toRgb(),
        }
      : {
          hsl: tinycolor("#7b61ff").toHsl(),
          hex: "#7b61ff",
          rgb: tinycolor("#7b61ff").toRgb(),
        },
  );

  const [updateColours, { data: updatedTheme, isSuccess: didUpdateColours }] =
    useUpdatePracticeThemeMutation();

  const [debouncedPrimary] = useDebounce(primaryColour, 400);
  const [debouncedSecondary] = useDebounce(secondaryColour, 400);

  const handleSave = () => {
    if (practiceId && primaryColour && secondaryColour) {
      updateColours({
        practiceId: practiceId,
        body: {
          primary_color: `${primaryColour.rgb.r} ${primaryColour.rgb.g} ${primaryColour.rgb.b}`,
          secondary_color: `${secondaryColour.rgb.r} ${secondaryColour.rgb.g} ${secondaryColour.rgb.b}`,
        },
      });
    }
  };

  useEffect(() => {
    if (debouncedPrimary && tinycolor(primaryColour.hex).isValid()) {
      const darkRGB = tinycolor({ ...primaryColour.hsl, l: 9 }).toRgb();
      dispatch(
        setTempColours({
          dark_color: `${darkRGB.r} ${darkRGB.g} ${darkRGB.b}`,
          on_dark_color: invert
            .asRgbArray(Object.values(darkRGB) as RgbArray, true)
            .join(" "),

          primary_color: `${primaryColour.rgb.r} ${primaryColour.rgb.g} ${primaryColour.rgb.b}`,
          on_primary_color: invert
            .asRgbArray(Object.values(primaryColour.rgb) as RgbArray, true)
            .join(" "),
        }),
      );
    }
  }, [debouncedPrimary]);

  useEffect(() => {
    if (debouncedSecondary && tinycolor(secondaryColour.hex).isValid()) {
      const lightestRGB = tinycolor({ ...secondaryColour.hsl, l: 90 }).toRgb();

      dispatch(
        setTempColours({
          light_color: `${secondaryColour.rgb.r} ${secondaryColour.rgb.g} ${secondaryColour.rgb.b}`,
          on_light_color: invert
            .asRgbArray(Object.values(secondaryColour.rgb) as RgbArray, true)
            .join(" "),

          lightest_color: `${lightestRGB.r} ${lightestRGB.g} ${lightestRGB.b}`,
          on_lightest_color: invert
            .asRgbArray(Object.values(lightestRGB) as RgbArray, true)
            .join(" "),
        }),
      );
    }
  }, [debouncedSecondary]);

  useEffect(() => {
    if (didUpdateColours && updatedTheme) {
      dispatch(
        setColours({
          dark_color: updatedTheme.dark_color,
          on_dark_color: updatedTheme.on_dark_color,
          primary_color: updatedTheme.primary_color,
          on_primary_color: updatedTheme.on_primary_color,
          light_color: updatedTheme.light_color,
          on_light_color: updatedTheme.on_light_color,
          lightest_color: updatedTheme.lightest_color,
          on_lightest_color: updatedTheme.on_lightest_color,
        }),
      );

      notify(
        <NotifierNotification
          title="Colours Updates"
          message="Practice theme colours have been updated"
        />,
      );
    }
  }, [didUpdateColours]);

  useEffect(() => {
    return () => {
      dispatch(clearTempColours());
    };
  }, []);

  return (
    <UnsavedChangesPrompt
      isDirty={
        (tinycolor(`rgb(${defaultPrimary})`).isValid()
          ? tinycolor(`rgb(${defaultPrimary})`).toString("hex")
          : "#3c289b") !== primaryColour.hex ||
        (tinycolor(`rgb(${defaultSecondary})`).isValid()
          ? tinycolor(`rgb(${defaultSecondary})`).toString("hex")
          : "#7b61ff") !== secondaryColour.hex
      }
    >
      <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
        <div className="col-start-1 mb-32 tablet:col-end-4 desktop:col-end-5">
          <ColourSection
            title="Primary Colour"
            description="This will be used on buttons and links."
            value={primaryColour?.hex ?? ""}
            onChange={(colours) => setPrimaryColour(colours)}
          />
        </div>

        <div className="tablet:col-start-5 tablet:col-end-8 desktop:col-start-6 desktop:col-end-10">
          <ColourSection
            title="Secondary Colour"
            description="This will be used on hover and highlights"
            value={secondaryColour?.hex ?? ""}
            onChange={(colours) => setSecondaryColour(colours)}
          />
        </div>

        <div className="col-span-full max-w-[185px]">
          <Button block type="button" onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </div>
    </UnsavedChangesPrompt>
  );
};

export default ColoursPage;
